import { Modal, ModalBody, ROLE, SIZE } from 'baseui/modal'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useState } from 'react'
import { StatusAlertSmall, WemoloButton, WemoloTypography } from '../../components/Wemolo'
import { DeviceTypeEnum } from '../../data/enums'
import { useVncViewer, VncConnectionStatus, VncMode } from './useVncViewer'
import styles from './VncLiveView.module.css'

interface VncLiveViewProps {
    /** Device VPN IP (from the wireguard config). */
    ip: string | null | undefined
    deviceType: string | null | undefined
    /** True while this tab is the active/visible one. */
    active: boolean
}

const alertVariantByStatus: Record<VncConnectionStatus, 'green' | 'yellow' | 'red' | 'gray'> = {
    connected: 'green',
    connecting: 'yellow',
    error: 'red',
    idle: 'gray',
    timeout: 'yellow'
}

const statusLabelByStatus: Record<VncConnectionStatus, string> = {
    connected: 'Live',
    connecting: 'Connecting…',
    error: 'Connection error',
    idle: 'Disconnected',
    timeout: 'Session ended'
}

interface ViewerSurfaceProps {
    ip: string
    mode: VncMode
    enabled: boolean
    fullscreen: boolean
    canInteract: boolean
    interactive: boolean
    /** When provided, shows the interactive on/off toggle (inline only — omitted in fullscreen). */
    onToggleInteractive?: () => void
    onOpenFullscreen?: () => void
}

// One noVNC connection + its toolbar. Rendered inline and (separately) inside the fullscreen modal;
// only the enabled instance holds a live connection, so the two never fight over the same socket.
const ViewerSurface: React.FC<ViewerSurfaceProps> = ({
    ip,
    mode,
    enabled,
    fullscreen,
    canInteract,
    interactive,
    onToggleInteractive,
    onOpenFullscreen
}) => {
    const { containerRef, status, error, reconnect } = useVncViewer({ ip, mode, enabled, scaleToFit: !fullscreen })

    const statusText =
        status === 'error' || status === 'timeout'
            ? error || statusLabelByStatus[status]
            : status === 'connected'
              ? `Live · ${mode === 'interactive' ? 'interactive' : 'view-only'}`
              : statusLabelByStatus[status]

    return (
        <div className={styles.root}>
            <div className={styles.toolbar}>
                <StatusAlertSmall variant={alertVariantByStatus[status]}>{statusText}</StatusAlertSmall>
                <div className={styles.controls}>
                    {canInteract && onToggleInteractive && (
                        <WemoloButton
                            view={interactive ? 'primary' : 'secondary'}
                            size="small"
                            onClick={onToggleInteractive}
                        >
                            {interactive ? 'Interactive: on' : 'Interactive: off'}
                        </WemoloButton>
                    )}
                    {(status === 'error' || status === 'idle' || status === 'timeout') && (
                        <WemoloButton view="secondary" size="small" onClick={reconnect}>
                            Reconnect
                        </WemoloButton>
                    )}
                    {onOpenFullscreen && (
                        <WemoloButton view="secondary" size="small" aria-label="Fullscreen" onClick={onOpenFullscreen}>
                            <i className="material-icons">fullscreen</i>
                        </WemoloButton>
                    )}
                </div>
            </div>
            <div ref={containerRef} className={fullscreen ? styles.screenFull : styles.screen} />
        </div>
    )
}

export const VncLiveView: React.FC<VncLiveViewProps> = ({ ip, deviceType, active }) => {
    const { vncInteractiveEnabled } = useFlags()
    // Only terminals expose an interactive session; screens stay view-only (per the device gateway).
    // The interactive toggle is additionally gated behind the vnc_interactive_enabled flag.
    const canInteract = Boolean(vncInteractiveEnabled) && deviceType === DeviceTypeEnum.TERMINAL
    const [interactive, setInteractive] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const mode: VncMode = canInteract && interactive ? 'interactive' : 'view'
    const subject = canInteract ? 'terminal' : 'screen'
    const toggleInteractive = () => setInteractive((on) => !on)

    if (!ip) {
        return (
            <WemoloTypography variant="body-medium" tag="p" className={styles.message}>
                No VPN IP is configured for this device, so the {subject} live view is unavailable.
            </WemoloTypography>
        )
    }

    return (
        <>
            <ViewerSurface
                ip={ip}
                mode={mode}
                enabled={active && !isFullscreen}
                fullscreen={false}
                canInteract={canInteract}
                interactive={interactive}
                onToggleInteractive={toggleInteractive}
                onOpenFullscreen={() => setIsFullscreen(true)}
            />
            <Modal
                isOpen={isFullscreen}
                onClose={() => setIsFullscreen(false)}
                closeable
                animate
                autoFocus
                size={SIZE.auto}
                role={ROLE.dialog}
            >
                <ModalBody className={styles.modalBody}>
                    {isFullscreen && (
                        <ViewerSurface
                            ip={ip}
                            mode={mode}
                            enabled
                            fullscreen
                            canInteract={canInteract}
                            interactive={interactive}
                        />
                    )}
                </ModalBody>
            </Modal>
        </>
    )
}
